/**
 * "Book" on a lead: pick an open slot (GET /api/public/booking/:slug/slots?date=, the same open times the booking page shows, in the
 * business's time zone) or any time by hand, then the owner booking route (POST /api/workspaces/:wsId/appointments with leadId) books
 * it. A clash comes back as 409 with a readable message; the owner can then book anyway.
 */
import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { CalendarClock, Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { useToast } from "@/hooks/use-toast";
import { cn } from "@/lib/utils";
import { getJson, leadsKey, sendJson } from "./api";

interface Slot { start: string; end: string; label?: string }
interface SlotsResponse { slots: Slot[]; timezone?: string; durationMins?: number; meetingTitle?: string }

const localDate = (d = new Date()) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

export default function BookDialog({ open, onOpenChange, workspaceId, lead }: {
  open: boolean; onOpenChange: (v: boolean) => void; workspaceId: number;
  lead: { id: number; name: string; email: string; phone: string | null };
}) {
  const { toast } = useToast();
  const qc = useQueryClient();
  const [date, setDate] = useState(localDate());
  const [picked, setPicked] = useState<Slot | null>(null);
  const [manual, setManual] = useState("");
  const [sendInvite, setSendInvite] = useState(true);
  const [clash, setClash] = useState<string | null>(null);

  useEffect(() => { if (open) { setPicked(null); setManual(""); setClash(null); setDate(localDate()); } }, [open]);

  const { data: ws } = useQuery<{ bookingSlug?: string | null } | null>({
    queryKey: ["/api/workspaces", workspaceId],
    queryFn: async () => {
      const res = await fetch(`/api/workspaces/${workspaceId}`, { credentials: "include" });
      return res.ok ? res.json() : null;
    },
    enabled: open,
    staleTime: 60_000,
  });
  const slug = ws?.bookingSlug ?? null;

  const slotsQ = useQuery<SlotsResponse>({
    queryKey: ["/api/public/booking", slug, "slots", date],
    queryFn: () => getJson(`/api/public/booking/${slug}/slots?date=${date}`),
    enabled: open && !!slug && /^\d{4}-\d{2}-\d{2}$/.test(date),
    retry: false,
    staleTime: 30_000,
  });
  const tz = slotsQ.data?.timezone;
  const slots = slotsQ.data?.slots ?? [];
  // the day is already picked, so a slot shows just its time (in the business's zone); the full label stays as the tooltip
  const label = (s: Slot) => {
    try { return new Date(s.start).toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit", ...(tz ? { timeZone: tz } : {}) }); } catch { return s.label || s.start; }
  };

  const startIso = useMemo(() => {
    if (picked) return picked.start;
    if (!manual) return null;
    const d = new Date(manual);
    return Number.isNaN(d.getTime()) ? null : d.toISOString();
  }, [picked, manual]);

  const book = useMutation({
    mutationFn: (allowClash: boolean) => sendJson("POST", `/api/workspaces/${workspaceId}/appointments`, {
      leadId: lead.id, startAt: startIso, endAt: picked?.end ?? null,
      attendeeName: lead.name, attendeeEmail: lead.email || null, attendeePhone: lead.phone || null,
      allowClash, sendInvite,
    }),
    onSuccess: (r: any) => {
      setClash(null);
      qc.invalidateQueries({ queryKey: leadsKey(workspaceId) });
      qc.invalidateQueries({ queryKey: ["/api/workspaces", workspaceId, "appointments"] });
      toast({ title: "Booked", description: r?.confirmationSent ? `${lead.name} was emailed the invite.` : sendInvite ? "Booked. The invite email did not go out; check the Calendar tab." : "Booked. No invite was emailed." });
      onOpenChange(false);
    },
    onError: (e: any) => {
      if (e?.status === 409) setClash(e.message);
      else toast({ title: "Couldn't book", description: e?.message, variant: "destructive" });
    },
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="dialog-book">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><CalendarClock size={18} /> Book {lead.name}</DialogTitle>
          <DialogDescription>Pick an open time{tz ? ` (${tz})` : ""} or enter one by hand.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1.5">
            <label className="text-xs text-muted-foreground" htmlFor="book-date">Day</label>
            <Input id="book-date" type="date" value={date} min={localDate()} onChange={(e) => { setDate(e.target.value); setPicked(null); }} data-testid="input-book-date" />
          </div>
          <div className="space-y-1.5">
            <div className="text-xs text-muted-foreground">Open times</div>
            {!slug ? (
              <p className="text-xs text-muted-foreground">Loading…</p>
            ) : slotsQ.isLoading ? (
              <div className="flex items-center gap-2 text-xs text-muted-foreground"><Loader2 size={12} className="animate-spin" /> Finding open times…</div>
            ) : slotsQ.isError ? (
              <p className="text-xs text-amber-400" data-testid="text-book-slots-error">{(slotsQ.error as Error)?.message}. Enter a time by hand below.</p>
            ) : slots.length === 0 ? (
              <p className="text-xs text-muted-foreground" data-testid="text-book-no-slots">No open times that day. Try another day, or enter a time by hand.</p>
            ) : (
              <div className="grid grid-cols-3 gap-1.5 max-h-48 overflow-y-auto" data-testid="book-slots">
                {slots.map((s) => (
                  <button
                    key={s.start}
                    type="button"
                    data-testid={`button-slot-${s.start}`}
                    title={s.label || undefined}
                    onClick={() => { setPicked(s); setManual(""); setClash(null); }}
                    className={cn("rounded-lg border px-2 py-1.5 text-xs transition-colors", picked?.start === s.start ? "border-blue-500/60 bg-blue-500/15 text-blue-300" : "border-border hover:bg-muted/50")}
                  >
                    {label(s)}
                  </button>
                ))}
              </div>
            )}
          </div>
          <div className="space-y-1.5">
            <label className="text-xs text-muted-foreground" htmlFor="book-manual">Or any time (your local time)</label>
            <Input id="book-manual" type="datetime-local" value={manual} onChange={(e) => { setManual(e.target.value); setPicked(null); setClash(null); }} data-testid="input-book-manual" />
          </div>
          <label className="flex items-center gap-2 text-sm">
            <Checkbox checked={sendInvite} onCheckedChange={(v) => setSendInvite(v === true)} data-testid="checkbox-book-send-invite" disabled={!lead.email} />
            Email the invite to {lead.email || "the lead (no email on file)"}
          </label>
          {clash && (
            <div className="rounded-lg border border-amber-500/30 bg-amber-500/10 p-3 text-xs text-amber-200" data-testid="text-book-clash">{clash}</div>
          )}
        </div>
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="button-book-cancel">Cancel</Button>
          {clash ? (
            <Button onClick={() => book.mutate(true)} disabled={!startIso || book.isPending} data-testid="button-book-anyway">
              {book.isPending && <Loader2 className="animate-spin" />} Book anyway
            </Button>
          ) : (
            <Button onClick={() => book.mutate(false)} disabled={!startIso || book.isPending} data-testid="button-book-confirm">
              {book.isPending && <Loader2 className="animate-spin" />} Book
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
